/**
 * Модель экрана «Что от меня ждут».
 *
 * Вынесена из разметки ради трёх мест, где смысл теряется легче всего:
 *
 * * **состояние хозяйства** — у него шесть значений, и «часть контуров» не
 *   равно ни «отчитались», ни «данных нет». Неизвестный код показывается
 *   неизвестным, а не подставляется ближайшим знакомым;
 * * **срок** — трёхзначен: срока нет / срок идёт / просрочено. `null` здесь
 *   означает «вопроса не существует», и превращать его в «не просрочено» —
 *   значит ответить на незаданный вопрос;
 * * **итог массового ввода** — «затронуто N, пропущено M» без слова «почему»
 *   выглядит как сбой. Пропуск здесь — это сработавшее правило монотонности.
 */

import type { BulkReportOut, FarmObligationOut } from "../../api/client";
import { denominatorsOf, type ProgressView } from "../../lib/progress";
import { farmObligationStateLabel } from "../../lib/workVocab";
import { countOf, WORDS } from "../../astra/lib/format";
import { defineMessages, type PlainMessage } from "../../i18n/define";
import { tr } from "../../i18n/runtime";

const m = defineMessages({
  hintNoData: {
    id: "canonWorks.model.state.noData.hint",
    defaultMessage: "Отчётов нет ни по одному контуру. Это отсутствие данных, а не невыполнение.",
    description: "Подсказка у состояния работы хозяйства «нет данных» (экран «Что от меня ждут», карточка работы)",
  },
  hintPartial: {
    id: "canonWorks.model.state.partial.hint",
    defaultMessage: "Отчитались не все контуры: работа заявлена частично.",
    description: "Подсказка у состояния работы хозяйства «частично»",
  },
  hintReported: {
    id: "canonWorks.model.state.reported.hint",
    defaultMessage: "Отчитались все контуры. Проверка — отдельная ось, и она ещё не пройдена.",
    description: "Подсказка у состояния работы хозяйства «отчитались»; ось — отдельная шкала на экране",
  },
  hintConfirmed: {
    id: "canonWorks.model.state.confirmed.hint",
    defaultMessage: "Все контуры отчитались и подтверждены проверкой.",
    description: "Подсказка у состояния работы хозяйства «подтверждено»",
  },
  hintClarify: {
    id: "canonWorks.model.state.clarify.hint",
    defaultMessage: "Проверка просит уточнения хотя бы по одному контуру.",
    description: "Подсказка у состояния работы хозяйства «нужно уточнение»",
  },
  hintConflict: {
    id: "canonWorks.model.state.conflict.hint",
    defaultMessage: "Отчёт и проверка расходятся хотя бы по одному контуру. Обе записи остаются в базе — спор не решён.",
    description: "Подсказка у состояния работы хозяйства «расхождение»",
  },
  unknown: {
    id: "canonWorks.model.state.unknown",
    defaultMessage: "неизвестно",
    description: "Подпись состояния работы хозяйства, когда код состояния не пришёл; строчными, коротко",
  },
  noDeadline: {
    id: "canonWorks.model.deadline.none",
    defaultMessage: "без срока",
    description: "Подпись срока работы: у работы нет срока (бейдж; строчными, коротко)",
  },
  noDeadlineHint: {
    id: "canonWorks.model.deadline.noneHint",
    defaultMessage: "У работы нет срока — просрочки по ней не существует.",
    description: "Подсказка у подписи «без срока»",
  },
  overdue: {
    id: "canonWorks.model.deadline.overdue",
    defaultMessage: "просрочено",
    description: "Подпись срока работы: срок прошёл, а отчитались не все (бейдж; строчными, коротко)",
  },
  overdueHint: {
    id: "canonWorks.model.deadline.overdueHint",
    defaultMessage: "Срок закончился, а о завершении сообщили не по всем контурам. Это утверждение про отчёт, а не про работу: молчание не является невыполнением.",
    description: "Подсказка у подписи «просрочено»",
  },
  closed: {
    id: "canonWorks.model.deadline.closed",
    defaultMessage: "окно закрыто",
    description: "Подпись срока работы: срок прошёл, о завершении сообщили по всем контурам (бейдж; строчными, коротко)",
  },
  closedHint: {
    id: "canonWorks.model.deadline.closedHint",
    defaultMessage: "Срок закончился, и о завершении сообщили по всем контурам. Просрочки нет — но и «в срок» это утверждает только про отчёт, а не про работу.",
    description: "Подсказка у подписи «окно закрыто»",
  },
  running: {
    id: "canonWorks.model.deadline.running",
    defaultMessage: "срок идёт",
    description: "Подпись срока работы: окно ещё не закончилось (бейдж; строчными, коротко)",
  },
  runningHint: {
    id: "canonWorks.model.deadline.runningHint",
    defaultMessage: "Окно ещё не закончилось: отсутствие отчёта означает «данных нет», а не «не выполнено».",
    description: "Подсказка у подписи «срок идёт»",
  },
  bulkAll: {
    id: "canonWorks.model.bulk.all",
    defaultMessage: "Записано: {applied} из {total}.",
    description: "Итог массового ввода отчёта (фермер): {applied} — «N работ на полях» (уже со словом), {total} — число",
  },
  bulkSkips: {
    id: "canonWorks.model.bulk.withSkips",
    defaultMessage: "Записано: {applied} из {total}. {parts}.",
    description: "Итог массового ввода отчёта, когда часть работ пропущена; {applied} — «N работ на полях», {total} — число, {parts} — объяснения пропусков через точку с запятой",
  },
  skipMore: {
    id: "canonWorks.model.bulk.skipMore",
    defaultMessage: "{count} пропущено — там уже отчитались о большем, и действие их не понизило",
    description: "Объяснение пропуска в итоге массового ввода: по работе уже есть более полный отчёт; {count} — число",
  },
  skipSame: {
    id: "canonWorks.model.bulk.skipSame",
    defaultMessage: "{count} пропущено — там уже сказано ровно это же",
    description: "Объяснение пропуска в итоге массового ввода: такой отчёт уже внесён; {count} — число",
  },
  progressInProgress: {
    id: "canonWorks.model.progress.inProgress",
    defaultMessage: "В процессе",
    description: "Подпись степени выполнения работы: начата, не закончена (бейдж)",
  },
  progressDone: {
    id: "canonWorks.model.progress.done",
    defaultMessage: "Выполнено",
    description: "Подпись степени выполнения работы: закончена (бейдж)",
  },
  progressNone: {
    id: "canonWorks.model.progress.none",
    defaultMessage: "нет отчёта",
    description: "Подпись степени выполнения, когда отчёта нет (это не «не начато»); строчными, коротко",
  },
  progressUnknown: {
    id: "canonWorks.model.progress.unknown",
    defaultMessage: "неизвестно: {code}",
    description: "Подпись степени выполнения с незнакомым кодом; {code} — код с сервера",
  },
});

const STATE_HINTS: Record<string, PlainMessage> = {
  no_data: m.hintNoData,
  partial: m.hintPartial,
  reported: m.hintReported,
  confirmed: m.hintConfirmed,
  needs_clarification: m.hintClarify,
  conflict: m.hintConflict,
};

export interface StateView {
  label: string;
  hint: string | null;
  known: boolean;
  /** Требует внимания человека: спор, уточнение или незакрытая работа. */
  attention: boolean;
}

/** Подпись состояния хозяйства. Незнакомый код остаётся незнакомым. */
export function stateView(code: string | null | undefined): StateView {
  if (!code) {
    return { label: tr(m.unknown), hint: null, known: false, attention: false };
  }
  const named = farmObligationStateLabel(code);
  if (!named.known) {
    return { label: named.label, hint: null, known: false, attention: false };
  }
  const hint = STATE_HINTS[code];
  return {
    label: named.label,
    hint: hint ? tr(hint) : null,
    known: true,
    attention: code === "conflict" || code === "needs_clarification",
  };
}

export interface DeadlineView {
  label: string;
  hint: string | null;
  overdue: boolean;
}

/**
 * Что означает срок работы. ЧЕТЫРЕ исхода, и ни один не выводится из другого:
 *
 * * срока нет — вопроса о просрочке не существует;
 * * срок идёт — отсутствие отчёта означает «ещё не срок»;
 * * срок прошёл без полного отчёта — просрочено;
 * * срок прошёл, о завершении сообщили — «окно закрыто». Это НЕ «срок идёт»:
 *   подписать закрытое окно словом «идёт» значит сказать неправду про
 *   календарь ради того, чтобы не сказать «просрочено».
 *
 * `window_elapsed` приходит с сервера и считается тем же горизонтом, что и
 * `overdue`. Сравнивать даты на клиенте нельзя: это было бы второе место, где
 * решается, закончилось ли окно.
 */
export function deadlineView(item: {
  overdue: boolean | null;
  window_to: string | null;
  window_elapsed?: boolean | null;
}): DeadlineView {
  if (item.overdue === null || item.window_to === null) {
    return {
      label: tr(m.noDeadline),
      hint: tr(m.noDeadlineHint),
      overdue: false,
    };
  }
  if (item.overdue) {
    return {
      label: tr(m.overdue),
      hint: tr(m.overdueHint),
      overdue: true,
    };
  }
  if (item.window_elapsed) {
    return {
      label: tr(m.closed),
      hint: tr(m.closedHint),
      overdue: false,
    };
  }
  return {
    label: tr(m.running),
    hint: tr(m.runningHint),
    overdue: false,
  };
}

/** Два знаменателя работы хозяйства: обязательства и контуры. Гектары — рядом. */
export function farmDenominators(item: FarmObligationOut): ProgressView[] {
  return denominatorsOf({
    obligations: item.obligations,
    contours: item.contours,
    // Знаменателя «хозяйства» на карточке одного хозяйства не существует:
    // показывать «1 из 1» значило бы изображать долю там, где её нет.
    actors: { total: 0, reported: 0, verified: 0 },
  }).slice(0, 2);
}

/**
 * Итог массового ввода словами. Пропуск объясняется всегда: без объяснения
 * «затронуто 3 из 11» читается как сбой, а это сработавшее правило —
 * массовое действие не понижает контур, о котором известно больше.
 */
export function bulkNote(result: BulkReportOut): string {
  const applied = countOf(result.applied, WORDS.obligation);
  if (result.skipped_more_advanced === 0 && result.skipped_same_level === 0) {
    return tr(m.bulkAll, { applied, total: result.obligations_total });
  }
  const parts: string[] = [];
  if (result.skipped_more_advanced > 0) {
    parts.push(tr(m.skipMore, { count: result.skipped_more_advanced }));
  }
  if (result.skipped_same_level > 0) {
    parts.push(tr(m.skipSame, { count: result.skipped_same_level }));
  }
  return tr(m.bulkSkips, {
    applied,
    total: result.obligations_total,
    parts: parts.join("; "),
  });
}

/**
 * Ось степени выполнения словами. Она независима от проверки и от срока:
 * «начали» и «закончили» обе дают «отчёт есть», и различает их только это
 * число. `no_report` — отсутствие данных, а не «не начато».
 */
export function progressSummary(buckets: ReadonlyArray<{ key: string; count: number }>): {
  done: number;
  inProgress: number;
  noReport: number;
} {
  const at = (key: string) =>
    buckets.find((bucket) => bucket.key === key)?.count ?? 0;
  return {
    done: at("done"),
    inProgress: at("in_progress"),
    noReport: at("no_report"),
  };
}

const PROGRESS_LABELS: Record<string, PlainMessage> = {
  in_progress: m.progressInProgress,
  done: m.progressDone,
};

/** Подпись степени выполнения. `null` — отчёта нет, и это не «не начато». */
export function progressLabel(code: string | null | undefined): {
  label: string;
  known: boolean;
} {
  if (!code) return { label: tr(m.progressNone), known: true };
  const label = PROGRESS_LABELS[code];
  return label
    ? { label: tr(label), known: true }
    : { label: tr(m.progressUnknown, { code }), known: false };
}

/**
 * Ось проверки словами — раздельно от оси выполнения и от «нет данных».
 *
 * Существует ради одной ошибки, которую легко сделать в разметке: показать
 * «проверено 0» рядом с «отчитались 27» и оставить читателя думать, что
 * двадцать семь контуров проверку не прошли. Не прошли — это `rejected`;
 * ноль здесь означает, что проверяющий до них ещё не доехал, и это третье
 * состояние, а не отсутствие второго.
 */
export interface VerificationSplit {
  /** Подтверждено проверкой. */
  verified: number;
  /** Отчёт есть, суждения нет: ждут проверяющего. */
  awaiting: number;
  /** Разобрано, но не подтверждено: спор или запрос уточнения. */
  disputed: number;
  /** Отчёта нет — эти контуры проверку не ждут, проверять нечего. */
  noReport: number;
}

export function verificationSplit(item: {
  obligations: { total: number; reported: number; verified: number };
  by_state: ReadonlyArray<{ key: string; count: number }>;
}): VerificationSplit {
  const at = (key: string) =>
    item.by_state.find((bucket) => bucket.key === key)?.count ?? 0;
  return {
    verified: item.obligations.verified,
    awaiting: at("reported"),
    disputed: at("conflict") + at("needs_clarification"),
    noReport: at("no_data"),
  };
}

/* ------------------------------------------------------------------ */
/* Ответ экрана фермера                                                */
/* ------------------------------------------------------------------ */

/** Работа, с которой разумно начать отчёт. */
export interface FarmerNextWork {
  id: number;
  workName: string;
  /** Конец окна. `null` — срока у работы нет, и это не «срок далёкий». */
  windowTo: string | null;
  overdue: boolean;
}

/**
 * Ответ первого взгляда фермера: сколько единиц ждут отчёта и с какой работы
 * начинать. Считается здесь, а не в разметке, ради трёх различений:
 *
 * * **ждёт отчёта — это `total − reported`, а не «не проверено».** Проверка —
 *   вторая ось, и работа, отчёт по которой уже подан, из ожидания уходит,
 *   даже если проверяющий до неё не доехал;
 * * **работа без срока не участвует в выборе «ближайшего».** Срок у неё не
 *   поздний, его нет вовсе, и подставлять её в календарь значит выдумать дату.
 *   Но действовать по ней можно, поэтому когда сроков нет НИ У ОДНОЙ, ответом
 *   становится первая ждущая работа с `windowTo === null`;
 * * **просрочку решает сервер.** Берётся тот же разбор, что и у подписи срока
 *   (`deadlineView`), — второе место, сравнивающее даты, однажды разошлось бы
 *   с первым.
 */
export interface FarmerHero {
  /** Контурных обязательств без отчёта. */
  awaiting: number;
  /** В скольких работах они лежат. */
  works: number;
  /** Из них — работы, у которых срока нет вовсе. */
  undated: number;
  /** С чего начать. `null` — ждущих работ нет. */
  next: FarmerNextWork | null;
}

export function farmerHero(
  items: ReadonlyArray<FarmObligationOut>,
): FarmerHero {
  let awaiting = 0;
  let works = 0;
  let undated = 0;
  let next: FarmerNextWork | null = null;
  let nextDate: string | null = null;
  let fallback: FarmerNextWork | null = null;

  for (const item of items) {
    const without = item.obligations.total - item.obligations.reported;
    if (without <= 0) continue;
    awaiting += without;
    works += 1;

    // Условие «срока нет» повторяет первую ветку `deadlineView`: у работы либо
    // нет конца окна, либо сервер не выносил суждения о просрочке.
    if (item.window_to === null || item.overdue === null) {
      undated += 1;
      fallback ??= {
        id: item.id,
        workName: item.work_name,
        windowTo: null,
        overdue: false,
      };
      continue;
    }
    // Даты ISO сравниваются как строки: порядок символов совпадает с порядком
    // дней, и разбирать их в `Date` ради сравнения незачем.
    if (nextDate === null || item.window_to < nextDate) {
      nextDate = item.window_to;
      next = {
        id: item.id,
        workName: item.work_name,
        windowTo: item.window_to,
        overdue: deadlineView(item).overdue,
      };
    }
  }

  return { awaiting, works, undated, next: next ?? fallback };
}
